(() => {
  if (window.EverNextPurchaseUX?.version === '1.0.4') return;

  const ROOT = window.Shopify?.routes?.root || '/';
  const GENERIC_COLLECTIONS = new Set(['all', 'frontpage', 'new-arrivals', 'best-sellers', 'gift-ideas']);
  const FAMILY_GROUPS = [
    ['bead', 'bracelet', 'jewelry'],
    ['miniature', 'dollhouse', 'book nook', 'model house'],
    ['crochet', 'knit', 'yarn'],
    ['puzzle'],
    ['paint', 'painting']
  ];
  let addInFlight = false;
  let cartRevision = 0;
  let refreshSequence = 0;
  let syncEnabled = false;
  const uncertainVariants = new Set();
  const pendingRecovery = new Map();
  const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const asNumber = (value) => Number.parseInt(String(value), 10) || 0;
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const productType = (product) => normalize(product.type || product.product_type);
  const productTitle = (product) => normalize(product.title);
  const productId = (product) => asNumber(product.id);
  const productUrl = (product) => product.url || `${ROOT}products/${product.handle}`;
  const productImage = (product) => {
    const featured = product.featured_image;
    if (typeof featured === 'string') return featured;
    if (featured?.url) return featured.url;
    if (featured?.src) return featured.src;
    return product.images?.[0]?.src || product.images?.[0] || '';
  };
  const variantPriceCents = (variant) => {
    const raw = variant?.price;
    if (typeof raw === 'number') return Math.round(raw);
    if (typeof raw === 'string' && raw.includes('.')) return Math.round(Number.parseFloat(raw) * 100);
    return asNumber(raw);
  };
  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    } catch (_error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };
  const availableVariants = (product) => (product.variants || []).filter((variant) => {
    const ships = variant.requires_shipping ?? variant.requiresShipping;
    return variant.available === true && ships !== false && variantPriceCents(variant) > 0;
  });
  const familyMatch = (left, right) => {
    const a = `${productType(left)} ${productTitle(left)}`;
    const b = `${productType(right)} ${productTitle(right)}`;
    return FAMILY_GROUPS.some((group) => group.some((token) => a.includes(token)) && group.some((token) => b.includes(token)));
  };
  const sameType = (left, right) => {
    const a = productType(left);
    const b = productType(right);
    return Boolean(a && b && a === b);
  };
  const meaningfulCollections = (handles) => (handles || []).map(normalize).filter((handle) => handle && !GENERIC_COLLECTIONS.has(handle));
  const serviceLike = (product) => {
    const value = `${productType(product)} ${productTitle(product)}`;
    return Boolean(product.gift_card || product.giftCard || /service|protection|warranty|gift card/.test(value));
  };
  const parseJson = (root, selector, fallback = []) => {
    try { return JSON.parse(root.querySelector(selector)?.textContent || '[]'); }
    catch (_error) { return fallback; }
  };
  const sectionIds = () => {
    const ids = new Set();
    document.querySelectorAll('[section-id][ref="hdt-cart"], evernext-cart-upsell[data-section-id]').forEach((node) => {
      const id = node.getAttribute('section-id') || node.dataset.sectionId;
      if (id) ids.add(id);
    });
    return [...ids].slice(0, 5);
  };
  const replaceSections = (sections) => {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      const currentCart = [...document.querySelectorAll('[section-id][ref="hdt-cart"]')]
        .find((node) => node.getAttribute('section-id') === id);
      const nextCart = template.content.querySelector('[section-id][ref="hdt-cart"]');
      if (currentCart?.querySelector && nextCart?.querySelector) {
        if (currentCart.tagName === 'HDT-CART-DRAWER') {
          const items = currentCart.querySelector('cart-items-component');
          const nextItems = nextCart.querySelector('cart-items-component');
          if (!items || !nextItems) throw new Error('Cart items are unavailable.');
          items.innerHTML = nextItems.innerHTML;
          for (const selector of ['.en-drawer-shipping-top', '[ref="shipping-bar"]']) {
            const current = currentCart.querySelector(selector);
            const next = nextCart.querySelector(selector);
            if (current && next) current.replaceWith(next);
            else if (current) current.remove();
            else if (next) currentCart.querySelector('.hdt-dialog-drawer__header')?.after(next);
          }
        } else {
          currentCart.innerHTML = nextCart.innerHTML;
        }
        currentCart.dataset.count = nextCart.dataset.count;
        currentCart.dataset.totalPrice = nextCart.dataset.totalPrice;
        currentCart.classList.toggle('is-empty', asNumber(nextCart.dataset.count) === 0);
        document.dispatchEvent(new CustomEvent(currentCart.tagName === 'HDT-CART-DRAWER' ? 'cart:drawer:render' : 'cart:main:render'));
        return;
      }
      const wrapper = document.getElementById(`shopify-section-${id}`);
      if (!wrapper) throw new Error('Cart section is unavailable.');
      const completeSection = template.content.querySelector(`[id="shopify-section-${id}"]`);
      if (completeSection) wrapper.replaceWith(completeSection);
      else wrapper.replaceChildren(template.content.cloneNode(true));
    });
  };
  const refreshedStatus = (source) => {
    if (source === 'cart_drawer') return document.querySelector('evernext-cart-upsell[data-surface="drawer"] [data-en-upsell-status]');
    if (source === 'cart_cart-page') return document.querySelector('evernext-cart-upsell[data-surface="cart-page"] [data-en-upsell-status]');
    if (source === 'pdp_bottom') return document.querySelector('evernext-product-bottom-buy [data-en-bottom-status]');
    return null;
  };
  const updateVisibleCount = (count, total) => {
    document.querySelectorAll('[data-cart-count], .hdt-cart-count, [data-cart-item-count], hdt-cart-count').forEach((node) => {
      node.textContent = String(count);
      node.hidden = count < 1;
    });
    document.querySelectorAll('[ref="hdt-cart"][data-count]').forEach((node) => node.dataset.count = String(count));
    document.dispatchEvent(new CustomEvent('cart:count:update', { detail: { item_count: count, total_price: money(total || 0) } }));
    document.dispatchEvent(new CustomEvent('currency:update'));
  };
  const openDrawer = () => {
    const dialog = document.getElementById('CartDrawer');
    if (!dialog || dialog.open) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
  };
  const readJson = async (url, isSection = false) => {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: isSection ? {} : { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart refresh failed.');
    return response.json();
  };
  const cartSignature = (cart) => JSON.stringify([cart.item_count, cart.total_price,
    (cart.items || []).map((item) => [item.key, item.variant_id || item.id, item.quantity, item.final_line_price])]);
  const variantQuantity = (cart, id) => (cart?.items || []).reduce((count, item) =>
    count + (asNumber(item.variant_id || item.id) === asNumber(id) ? asNumber(item.quantity) : 0), 0);
  const sectionContext = () => {
    const preview = (window.location.search || '').match(/[?&]preview_theme_id=(\d+)/);
    return window.location.pathname + (preview ? `?preview_theme_id=${preview[1]}` : '');
  };
  const sectionSnapshot = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    try { return JSON.parse(template.content.querySelector('[data-en-cart-state]')?.textContent || 'null'); }
    catch (_error) { return null; }
  };
  const refreshCurrentCart = async (ids = sectionIds(), bundled = null, writeRevision = cartRevision) => {
    const sequence = ++refreshSequence;
    if (bundled && ids.length && ids.every((id) => typeof bundled[id] === 'string')) {
      const cart = await readJson(`${ROOT}cart.js`);
      if (sequence !== refreshSequence) return null;
      if (writeRevision === cartRevision && ids.every((id) => {
        const snapshot = sectionSnapshot(bundled[id]);
        return snapshot && cartSignature(snapshot) === cartSignature(cart);
      })) {
        replaceSections(bundled);
        updateVisibleCount(cart.item_count, cart.total_price);
        return cart;
      }
    }
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const revision = cartRevision;
      const before = await readJson(`${ROOT}cart.js`);
      const context = sectionContext();
      const sections = ids.length ? await readJson(`${context}${context.includes('?') ? '&' : '?'}sections=${ids.map(encodeURIComponent).join(',')}`, true) : null;
      const after = ids.length ? await readJson(`${ROOT}cart.js`) : before;
      if (sequence !== refreshSequence) return null;
      if (revision !== cartRevision || cartSignature(before) !== cartSignature(after)) continue;
      if (sections && ids.some((id) => typeof sections[id] !== 'string' || !sections[id].trim())) throw new Error('Cart section refresh incomplete.');
      replaceSections(sections);
      updateVisibleCount(after.item_count, after.total_price);
      return after;
    }
    throw new Error('Cart is still updating.');
  };
  const recoverCart = async (job) => {
    if (job.running) return null;
    job.running = true;
    try {
      for (const delay of [250, 750, 1500]) {
        await pause(delay);
        try {
          const cart = await refreshCurrentCart();
          if (!cart) continue;
          const status = document.contains(job.status) ? job.status : refreshedStatus(job.source);
          if (status) status.textContent = job.acknowledged ? 'Added to cart.' : 'Cart updated. Check the item quantity before adding again.';
          if (job.acknowledged) {
            uncertainVariants.delete(job.id);
            if (document.contains(job.button)) job.button.disabled = false;
          }
          pendingRecovery.delete(job.id);
          return cart;
        } catch (_error) {}
      }
      const status = document.contains(job.status) ? job.status : refreshedStatus(job.source);
      if (status) status.textContent = job.acknowledged
        ? 'Added to cart. Connection interrupted; your cart will sync when you reconnect.'
        : 'We could not confirm this add. Your cart will sync when you reconnect; please do not add again.';
      return null;
    } finally { job.running = false; }
  };
  window.addEventListener?.('online', () => pendingRecovery.forEach((job) => recoverCart(job)));
  // Kalles mutation events, verified in assets/events.js. Do not subscribe to
  // render notifications: replacing sections can emit them again on remount.
  ['cart:update', 'discount:update'].forEach((name) => {
    document.addEventListener?.(name, () => {
      cartRevision += 1;
      if (syncEnabled) Promise.resolve().then(() => refreshCurrentCart()).catch(() => {
        const job = { id: 'native', acknowledged: false, source: 'unknown' };
        if (!pendingRecovery.has('native')) { pendingRecovery.set('native', job); recoverCart(job); }
      });
    });
  });
  const addItem = async ({ variantId, quantity = 1, button, status, openCart = true, source = 'unknown' }) => {
    const id = asNumber(variantId);
    if (uncertainVariants.has(id)) {
      if (button) button.disabled = true;
      if (status) status.textContent = 'Check your cart before adding this item again.';
      return false;
    }
    if (addInFlight) return false;
    addInFlight = true;
    if (button) button.disabled = true;
    if (status) { status.textContent = 'Adding…'; status.classList.remove('is-error'); }
    let writeSucceeded = false;
    let writeAttempted = false;
    let explicitlyRejected = false;
    let keepDisabled = false;
    let beforeCart = null;
    const ids = sectionIds();
    const beforePromise = readJson(`${ROOT}cart.js`).catch(() => null);
    try {
      syncEnabled = true;
      writeAttempted = true;
      const writeRevision = cartRevision;
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id, quantity: Math.max(1, asNumber(quantity)) }],
          sections: ids,
          sections_url: sectionContext()
        })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        explicitlyRejected = response.status >= 400 && response.status < 500;
        throw new Error(result.description || result.message || 'This item could not be added.');
      }
      writeSucceeded = true;
      // Bundled HTML is usable only when its complete cart snapshot still matches.
      const cart = await refreshCurrentCart(ids, result.sections, writeRevision);
      if (!cart) throw new Error('A newer cart refresh is in progress.');
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart, source } }));
      const successStatus = document.contains(status) ? status : refreshedStatus(source);
      if (successStatus) successStatus.textContent = 'Added to cart.';
      if (openCart) requestAnimationFrame(openDrawer);
      return true;
    } catch (error) {
      if (writeSucceeded) {
        keepDisabled = true;
        uncertainVariants.add(id);
        const successStatus = document.contains(status) ? status : refreshedStatus(source);
        if (successStatus) successStatus.textContent = 'Added. Syncing your cart…';
        if (openCart) requestAnimationFrame(openDrawer);
        const job = { id, button, status: successStatus, source, acknowledged: true };
        pendingRecovery.set(id, job);
        if (await recoverCart(job)) keepDisabled = false;
        return true;
      }
      if (writeAttempted && !explicitlyRejected) {
        keepDisabled = true;
        uncertainVariants.add(id);
        if (status && document.contains(status)) status.textContent = 'Checking your cart…';
        beforeCart = await beforePromise;
        let afterCart = null;
        try { afterCart = await refreshCurrentCart(ids); } catch (_refreshError) {
          const job = { id, button, status, source, acknowledged: false };
          pendingRecovery.set(id, job);
          afterCart = await recoverCart(job);
        }
        const confirmed = beforeCart && afterCart && Array.isArray(beforeCart.items) && Array.isArray(afterCart.items)
          && variantQuantity(afterCart, id) - variantQuantity(beforeCart, id) >= Math.max(1, asNumber(quantity));
        const currentStatus = document.contains(status) ? status : refreshedStatus(source);
        if (currentStatus) currentStatus.textContent = confirmed
          ? 'Added to cart. Check your cart before adding this item again.'
          : 'We could not confirm this add. Check your cart before adding this item again.';
        if (openCart) requestAnimationFrame(openDrawer);
        return Boolean(confirmed);
      }
      if (status && document.contains(status)) {
        status.textContent = error.message || 'Unable to add this item. Please try again.';
        status.classList.add('is-error');
      }
      return false;
    } finally {
      addInFlight = false;
      if (button && document.contains(button)) button.disabled = keepDisabled;
    }
  };

  class EverNextCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._controller = new AbortController();
      this._onClick = this.onClick.bind(this);
      this.addEventListener('click', this._onClick);
      this.load();
    }
    disconnectedCallback() {
      this._controller?.abort();
      this.removeEventListener('click', this._onClick);
      this._connected = false;
    }
    async load() {
      const total = asNumber(this.dataset.cartTotal);
      const threshold = asNumber(this.dataset.freeShippingThreshold) || 3900;
      if (!total || total >= threshold) return;
      const seeds = [...new Map(parseJson(this, '[data-en-upsell-seeds]').map((seed) => [asNumber(seed.id), seed])).values()];
      const cartIds = new Set(parseJson(this, '[data-en-upsell-cart-ids]').map(asNumber));
      if (!seeds.length) return;
      const candidateMap = new Map();
      const addCandidate = (product, seed, origin, collectionHandle = '') => {
        const id = productId(product);
        if (!id || cartIds.has(id) || product.available === false || serviceLike(product) || !product.handle) return;
        const sharedCollection = Boolean(collectionHandle && meaningfulCollections(seed.collections).includes(normalize(collectionHandle)));
        const typeRelated = sameType(product, seed);
        const familyRelated = familyMatch(product, seed);
        if (!typeRelated && !familyRelated) return;
        const previous = candidateMap.get(id) || { product, relevance: 0, origins: new Set() };
        previous.product = product;
        previous.origins.add(origin);
        previous.relevance += (typeRelated ? 100 : 0) + (sharedCollection ? 30 : 0) + (familyRelated ? 60 : 0) + (origin === 'native' ? 10 : 0);
        candidateMap.set(id, previous);
      };
      const fetchJson = async (url) => {
        const response = await fetch(url, { credentials: 'same-origin', signal: this._controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      };
      try {
        await Promise.allSettled(seeds.map(async (seed) => {
          const url = `${ROOT}recommendations/products.json?product_id=${encodeURIComponent(seed.id)}&limit=10&intent=related`;
          const payload = await fetchJson(url);
          (payload.products || []).forEach((product) => addCandidate(product, seed, 'native'));
        }));

        const gap = Math.max(0, threshold - total);
        const hydrationCache = new Map();
        const hydrate = (entry) => {
          const handle = entry.product.handle;
          if (!hydrationCache.has(handle)) {
            hydrationCache.set(handle, fetchJson(`${ROOT}products/${encodeURIComponent(handle)}.js`).catch(() => null));
          }
          return hydrationCache.get(handle).then((product) => {
            if (!product || productId(product) !== productId(entry.product)) return null;
            return { ...entry, product };
          });
        };
        const rankCandidates = async () => {
          const hydrated = await Promise.all([...candidateMap.values()].map(hydrate));
          return hydrated.filter(Boolean).map((entry) => {
            const variants = availableVariants(entry.product);
            if (!variants.length) return null;
            const price = Math.min(...variants.map(variantPriceCents));
            return { ...entry, variants, price, meetsGap: price >= gap, distance: Math.abs(price - gap) };
          }).filter(Boolean).sort((a, b) => {
            if (a.relevance !== b.relevance) return b.relevance - a.relevance;
            if (a.meetsGap !== b.meetsGap) return a.meetsGap ? -1 : 1;
            if (a.distance !== b.distance) return a.distance - b.distance;
            return a.price - b.price;
          }).slice(0, 2);
        };

        let ranked = await rankCandidates();
        if (ranked.length < 2) {
          const collectionSeeds = new Map();
          seeds.forEach((seed) => meaningfulCollections(seed.collections).forEach((handle) => {
            if (!collectionSeeds.has(handle)) collectionSeeds.set(handle, []);
            collectionSeeds.get(handle).push(seed);
          }));
          for (const [handle, relatedSeeds] of collectionSeeds) {
            try {
              const payload = await fetchJson(`${ROOT}collections/${encodeURIComponent(handle)}/products.json?limit=50`);
              (payload.products || []).forEach((product) => relatedSeeds.forEach((seed) => addCandidate(product, seed, 'collection', handle)));
            } catch (_collectionError) {
              continue;
            }
          }
          ranked = await rankCandidates();
        }
        if (!ranked.length) return;
        this.render(ranked);
        this.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.hidden = true;
          this.dataset.enUpsellError = 'true';
        }
      }
    }
    render(entries) {
      const container = this.querySelector('[data-en-upsell-cards]');
      container.replaceChildren(...entries.map((entry) => {
        const product = entry.product;
        const article = document.createElement('article');
        article.className = 'en-cart-upsell__card';
        const media = document.createElement('a');
        media.className = 'en-cart-upsell__media';
        media.href = productUrl(product);
        const imageUrl = productImage(product);
        if (imageUrl) {
          const image = document.createElement('img');
          const sizedImage = (width) => /[?&]width=\d+/.test(imageUrl)
            ? imageUrl.replace(/([?&])width=\d+/, `$1width=${width}`)
            : `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=${width}`;
          image.src = sizedImage(240);
          image.srcset = `${sizedImage(80)} 80w, ${sizedImage(160)} 160w, ${sizedImage(240)} 240w`;
          image.sizes = '80px';
          image.width = 80;
          image.height = 80;
          image.alt = product.title || '';
          image.loading = 'lazy';
          media.append(image);
        }
        const body = document.createElement('div');
        body.className = 'en-cart-upsell__body';
        const title = document.createElement('a');
        title.className = 'en-cart-upsell__title';
        title.href = productUrl(product);
        title.textContent = product.title || '';
        const price = document.createElement('p');
        price.className = 'en-cart-upsell__price';
        price.textContent = money(entry.price);
        let action;
        if ((product.variants || []).length === 1) {
          action = document.createElement('button');
          action.type = 'button';
          action.className = 'en-cart-upsell__action';
          action.dataset.enUpsellAdd = '';
          action.dataset.variantId = entry.variants[0].id;
          action.textContent = 'Add';
        } else {
          action = document.createElement('a');
          action.className = 'en-cart-upsell__action';
          action.href = productUrl(product);
          action.textContent = 'Choose options';
        }
        body.append(title, price, action);
        article.append(media, body);
        return article;
      }));
    }
    async onClick(event) {
      const button = event.target.closest('[data-en-upsell-add]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      await addItem({ variantId: button.dataset.variantId, quantity: 1, button, status: this.querySelector('[data-en-upsell-status]'), openCart: this.dataset.surface === 'drawer', source: `cart_${this.dataset.surface}` });
    }
  }

  class EverNextProductBottomBuy extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.variants = parseJson(this, '[data-en-bottom-variants]');
      this.variantControl = this.querySelector('[data-en-bottom-variant]');
      this.quantityControl = this.querySelector('[data-en-bottom-quantity]');
      this.button = this.querySelector('[data-en-bottom-add]');
      this.status = this.querySelector('[data-en-bottom-status]');
      this._onChange = this.onChange.bind(this);
      this._onDocumentChange = this.onDocumentChange.bind(this);
      this._onVariantEvent = this.onVariantEvent.bind(this);
      this._onClick = this.onClick.bind(this);
      this.addEventListener('change', this._onChange);
      this.addEventListener('click', this._onClick);
      document.addEventListener('change', this._onDocumentChange);
      document.addEventListener('input', this._onDocumentChange);
      ['variant:change', 'variant:changed', 'product:variant-change', 'hdt:variant:changed'].forEach((name) => document.addEventListener(name, this._onVariantEvent));
      if (this.variantControl?.value || this.button?.dataset.variantId) this.syncVariant(this.variantControl?.value || this.button?.dataset.variantId);
    }
    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('click', this._onClick);
      document.removeEventListener('change', this._onDocumentChange);
      document.removeEventListener('input', this._onDocumentChange);
      ['variant:change', 'variant:changed', 'product:variant-change', 'hdt:variant:changed'].forEach((name) => document.removeEventListener(name, this._onVariantEvent));
      this._connected = false;
    }
    variant(id) { return this.variants.find((variant) => asNumber(variant.id) === asNumber(id)); }
    syncVariant(id) {
      const variant = this.variant(id);
      if (!variant) {
        this.button.dataset.variantId = '';
        this.button.disabled = true;
        this.querySelector('[data-en-bottom-add-label]').textContent = 'Choose an option';
        return;
      }
      if (this.variantControl) this.variantControl.value = String(variant.id);
      this.button.dataset.variantId = variant.id;
      this.button.disabled = !variant.available;
      this.querySelector('[data-en-bottom-add-label]').textContent = variant.available ? 'Add to cart' : 'Sold out';
      this.querySelector('[data-en-bottom-price]').textContent = variant.priceFormatted || money(asNumber(variant.price));
      this.quantityControl.min = variant.min || 1;
      this.quantityControl.step = variant.step || 1;
      if (variant.max) this.quantityControl.max = variant.max;
      else this.quantityControl.removeAttribute('max');
      if (asNumber(this.quantityControl.value) < asNumber(this.quantityControl.min)) this.quantityControl.value = this.quantityControl.min;
    }
    onChange(event) {
      if (event.target === this.variantControl) {
        this._bottomSelectionTouched = true;
        this.syncVariant(event.target.value);
        this.syncMainForm(event.target.value, this.quantityControl.value);
      }
      if (event.target === this.quantityControl) {
        const min = asNumber(event.target.min) || 1;
        const max = asNumber(event.target.max);
        let value = Math.max(min, asNumber(event.target.value));
        if (max) value = Math.min(max, value);
        event.target.value = value;
        this.syncMainForm(this.button.dataset.variantId, value);
      }
    }
    mainForm() {
      const sticky = document.querySelector('hdt-sticky-btn-atc[form]');
      const controlled = sticky?.getAttribute('form') ? document.getElementById(sticky.getAttribute('form')) : null;
      if (controlled?.matches('form[action*="/cart/add"]')) return controlled;
      return [...document.querySelectorAll('form[id^="product-form-"][action*="/cart/add"], .hdt-main-product-form form[action*="/cart/add"], product-form form[action*="/cart/add"]')]
        .find((form) => {
          const formId = form.getAttribute('id') || '';
          return !this.contains(form) && !formId.startsWith('form-product-sticky') && !formId.includes('gift');
        });
    }
    syncMainForm(variantId, quantity) {
      const form = this.mainForm();
      if (!form) return;
      const variant = this.variant(variantId);
      const formId = form.getAttribute('id') || '';
      const associated = formId ? [...document.querySelectorAll(`[form="${CSS.escape(formId)}"]`)] : [];
      const optionControls = [...form.querySelectorAll('[name^="options["]'), ...associated.filter((control) => control.name?.startsWith('options['))];
      const optionGroups = [...new Set(optionControls.map((control) => control.name))];
      (variant?.options || []).forEach((value, index) => {
        const group = optionGroups[index];
        optionControls.filter((control) => control.name === group).forEach((control) => {
          if (control.tagName === 'SELECT') control.value = value;
          else if (control.type === 'radio' || control.type === 'checkbox') control.checked = control.value === value;
        });
        const changed = optionControls.find((control) => control.name === group && (control.tagName === 'SELECT' || control.checked));
        if (changed) {
          changed.dispatchEvent(new Event('input', { bubbles: true }));
          changed.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      const idInput = form.querySelector('[name="id"]');
      if (idInput && variantId) {
        idInput.value = variantId;
        idInput.dispatchEvent(new Event('input', { bubbles: true }));
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const quantityInput = form.querySelector('[name="quantity"]');
      if (quantityInput && quantity) {
        quantityInput.value = quantity;
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    onDocumentChange(event) {
      if (this.contains(event.target)) return;
      const form = event.target.closest?.('form[action*="/cart/add"]');
      if (!form || (form.getAttribute('id') || '').startsWith('form-product-sticky')) return;
      requestAnimationFrame(() => {
        const id = form.querySelector('[name="id"]')?.value;
        const quantity = form.querySelector('[name="quantity"]')?.value;
        if (id && this.variant(id)) this.syncVariant(id);
        if (quantity) this.quantityControl.value = quantity;
      });
    }
    onVariantEvent(event) {
      const id = event.detail?.variant?.id || event.detail?.id || event.detail?.variantId;
      if (id && this.variant(id)) this.syncVariant(id);
    }
    async onClick(event) {
      const button = event.target.closest('[data-en-bottom-add]');
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      if (!button.dataset.variantId) {
        this.status.textContent = 'Choose an option first.';
        this.status.classList.add('is-error');
        this.variantControl?.focus();
        return;
      }
      this.syncMainForm(button.dataset.variantId, this.quantityControl.value);
      await addItem({ variantId: button.dataset.variantId, quantity: this.quantityControl.value, button, status: this.status, openCart: true, source: 'pdp_bottom' });
    }
  }

  window.EverNextPurchaseUX = { version: '1.0.4', addItem };
  if (!customElements.get('evernext-cart-upsell')) customElements.define('evernext-cart-upsell', EverNextCartUpsell);
  if (!customElements.get('evernext-product-bottom-buy')) customElements.define('evernext-product-bottom-buy', EverNextProductBottomBuy);
})();

(() => {
  const start = () => {
    if (!document.body.classList.contains('hdt-page-type-product') || window.EverNextPdpPurchaseGuard) return;
    const state = { version: '2.1.0', dismissals: 0, active: false };
    window.EverNextPdpPurchaseGuard = state;
    const dismissed = new WeakSet();
    const observed = new WeakSet();
    let lastPurchaseFocus = null;
    let marketingRequested = false;
    let frame = 0;
    const onScreen = (element) => {
      if (!element?.isConnected) return false;
      const rect = element.getBoundingClientRect();
      const style = getComputedStyle(element);
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth && style.display !== 'none' && style.visibility !== 'hidden';
    };
    const purchaseTarget = (element) => {
      if (!(element instanceof Element)) return false;
      if (element.closest('[data-en-purchase-zone], hdt-sticky-btn-atc')) return true;
      const control = element.closest('input[name="quantity"], [name^="options["]');
      return !!control?.form?.classList.contains('hdt-main-product-form');
    };
    const syncHints = () => {
      document.querySelectorAll('[data-en-standard-checkout-hint]').forEach((hint) => {
        const group = hint.closest('.en-direct-checkout');
        const hasMore = !!group?.querySelector('more-payment-options-link');
        const value = hasMore ? hint.dataset.enMoreHint : hint.dataset.enOtherHint;
        if (value && hint.textContent !== value) hint.textContent = value;
      });
    };
    const check = () => {
      frame = 0;
      syncHints();
      const focus = document.activeElement;
      const zones = [...document.querySelectorAll('[data-en-purchase-zone]')];
      const drawerOpen = !!document.querySelector('#CartDrawer:modal');
      state.active = zones.some(onScreen) || (purchaseTarget(focus) && onScreen(focus)) || drawerOpen;
      document.body.dataset.enPurchaseActive = String(state.active);
      if (!state.active || (marketingRequested && !drawerOpen)) return;
      document.querySelectorAll('.react-responsive-modal-root').forEach((root) => {
        if (!root.querySelector('.ecomsend__Modal__Container')) return;
        const close = root.querySelector('button.ecomsend__Modal__CloseButton');
        if (!close || close.disabled || dismissed.has(close)) return;
        const wasModalFocus = root.contains(document.activeElement);
        const restore = lastPurchaseFocus;
        dismissed.add(close);
        close.click();
        state.dismissals += 1;
        if (wasModalFocus && restore?.isConnected && restore.matches('input, select, textarea')) {
          setTimeout(() => {
            const current = document.activeElement;
            const otherDialog = document.querySelector('dialog:modal');
            if (!otherDialog && onScreen(restore) && (current === document.body || root.contains(current))) restore.focus({ preventScroll: true });
          }, 250);
        }
      });
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(check); };
    const intersection = new IntersectionObserver(schedule);
    const bind = () => {
      document.querySelectorAll('[data-en-purchase-zone]').forEach((zone) => {
        if (!observed.has(zone)) { observed.add(zone); intersection.observe(zone); }
      });
    };
    document.addEventListener('focusin', (event) => {
      if (purchaseTarget(event.target)) { lastPurchaseFocus = event.target; marketingRequested = false; }
      schedule();
    });
    document.addEventListener('pointerdown', (event) => {
      if (event.target.closest('[class*="ecomsend__"]') && !event.target.closest('.react-responsive-modal-root')) marketingRequested = true;
      if (purchaseTarget(event.target)) marketingRequested = false;
      if (purchaseTarget(event.target) && event.target.matches('input, select, textarea')) lastPurchaseFocus = event.target;
      schedule();
    }, { passive: true });
    document.addEventListener('change', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    new MutationObserver(() => { bind(); schedule(); }).observe(document.body, { childList: true, subtree: true });
    bind();
    schedule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

(() => {
  if (window.EverNextCartPendingGuard) return;
  const state = { version: '1.0.0', pending: false, generation: 0, blocked: 0, recovered: 0 };
  window.EverNextCartPendingGuard = state;
  const keyPlans = new Map();
  const variantPlans = new Map();
  const locks = new WeakMap();
  const walletLocks = new WeakMap();
  let lastIntent = 0;
  let latestCart = null;
  let rendered = false;
  let timer = 0;
  let frame = 0;
  let recovering = false;
  let recoveryDeferred = false;
  let verifying = false;
  let verificationFailed = false;
  let activeSection = '';
  const root = () => [...document.querySelectorAll('hdt-main-cart, hdt-cart-drawer')].find(n => n.getAttribute('section-id') === activeSection) || document.querySelector('hdt-main-cart, hdt-cart-drawer');
  const selectSurface = element => { const surface = element?.closest('hdt-main-cart, hdt-cart-drawer'); if (surface) activeSection = surface.getAttribute('section-id'); };
  const fullCart = cart => Array.isArray(cart?.items) && Number.isInteger(cart.item_count) && cart.item_count >= 0 && Number.isInteger(cart.total_price) && cart.total_price >= 0
    && cart.items.every(item => typeof item.key === 'string' && item.key.length > 0 && Number.isInteger(item.quantity) && item.quantity > 0)
    && cart.items.reduce((n, item) => n + item.quantity, 0) === cart.item_count;
  const snapshot = () => {
    try { return JSON.parse(root()?.querySelector('[data-en-cart-state]')?.textContent || '{"item_count":0,"total_price":0,"items":[]}'); }
    catch (_error) { return null; }
  };
  const labels = () => {
    try { return JSON.parse(document.querySelector('[data-en-cart-guard-labels]')?.textContent || '{}'); }
    catch (_error) { return {}; }
  };
  const buttons = () => [...document.querySelectorAll('button[name="checkout"]')].filter(n => n.closest('hdt-main-cart, hdt-cart-drawer'));
  const status = (error = false) => {
    const target = buttons()[0];
    if (!target) return;
    let message = target.parentElement.querySelector('.en-cart-update-status');
    if (!message) {
      message = document.createElement('p');
      message.className = 'en-cart-update-status';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      target.parentElement.append(message);
    }
    message.textContent = error ? labels().retry || '' : labels().updating || '';
    message.hidden = !state.pending && !error;
    let retry = target.parentElement.querySelector('.en-cart-update-retry');
    if (error && state.pending && !retry) {
      retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'en-cart-update-retry';
      retry.textContent = labels().refresh || '';
      retry.addEventListener('click', () => { verificationFailed = false; recover(state.generation); });
      target.parentElement.append(retry);
    }
    if (retry) retry.hidden = !state.pending || !error;
  };
  const setLocks = () => {
    if (!state.pending) return;
    buttons().forEach(button => {
      if (!locks.has(button)) locks.set(button, { inert: button.inert, ariaDisabled: button.getAttribute('aria-disabled'), busy: button.getAttribute('aria-busy') });
      if (!button.inert) button.inert = true;
      if (button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
      if (!button.hasAttribute('data-en-checkout-pending')) button.setAttribute('data-en-checkout-pending', '');
      if (button.getAttribute('aria-busy') !== 'true') button.setAttribute('aria-busy', 'true');
    });
    document.querySelectorAll('.en-cart-wallets').forEach(group => {
      if (!walletLocks.has(group)) walletLocks.set(group, group.inert);
      if (!group.inert) group.inert = true;
    });
  };
  const unlock = (error = false) => {
    clearTimeout(timer);
    state.pending = false;
    recoveryDeferred = false;
    keyPlans.clear();
    variantPlans.clear();
    buttons().forEach(button => {
      const previous = locks.get(button);
      if (!previous) return;
      button.inert = previous.inert;
      button.removeAttribute('data-en-checkout-pending');
      if (button.disabled) button.setAttribute('aria-disabled', 'true');
      else if (previous.ariaDisabled === null) button.removeAttribute('aria-disabled');
      else button.setAttribute('aria-disabled', previous.ariaDisabled);
      if (previous.busy === null) button.removeAttribute('aria-busy');
      else button.setAttribute('aria-busy', previous.busy);
      locks.delete(button);
    });
    document.querySelectorAll('.en-cart-wallets').forEach(group => {
      if (walletLocks.has(group)) { group.inert = walletLocks.get(group); walletLocks.delete(group); }
    });
    document.querySelectorAll('.en-cart-update-status').forEach(message => { message.hidden = !error; });
    document.querySelectorAll('.en-cart-update-retry').forEach(button => { button.hidden = true; });
    if (error) status(true);
  };
  const signature = cart => JSON.stringify([cart?.item_count, cart?.total_price, (cart?.items || []).map(i => [i.key, i.quantity])]);
  const matchesPlans = cart => cart && [...keyPlans].every(([key, qty]) => (cart.items.find(i => i.key === key)?.quantity || 0) === qty)
    && [...variantPlans].every(([id, qty]) => cart.items.filter(i => Number(i.variant_id || i.id) === id).reduce((n, i) => n + i.quantity, 0) === qty);
  const visibleMatches = cart => {
    const surface = root();
    if (!surface) return false;
    const inputs = [...surface.querySelectorAll('input[name="updates[]"]')];
    if (inputs.some(input => Number(input.value) !== cart.items[Number(input.dataset.index) - 1]?.quantity)) return false;
    const total = surface.querySelector('.hdt-totals__total-value');
    if (!cart.item_count) return !inputs.length && !total;
    if (!total) return false;
    const digits = (total.textContent || '').replace(/\D/g, '');
    const decimals = new Intl.NumberFormat('en', { style: 'currency', currency: cart.currency || latestCart?.currency || window.Shopify?.currency?.active || 'USD' }).resolvedOptions().maximumFractionDigits;
    return Number(digits) === Math.round(cart.total_price / 100 * 10 ** decimals);
  };
  const check = () => {
    frame = 0;
    if (!state.pending) return;
    setLocks();
    if (recoveryDeferred && !document.querySelector('cart-items-component.cart-items-disabled')) {
      recoveryDeferred = false;
      recover(state.generation);
      return;
    }
    const current = snapshot();
    if (performance.now() - lastIntent < 350 || !rendered || document.querySelector('cart-items-component.cart-items-disabled')) return;
    if (!matchesPlans(current) || !visibleMatches(current)) return;
    if (!latestCart) {
      if (!verifying && !verificationFailed) {
        verifying = true;
        const generation = state.generation;
        fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(5000) })
          .then(response => { if (!response.ok) throw new Error('Cart readback unavailable'); return response.json(); })
          .then(cart => { if (!fullCart(cart)) throw new Error('Incomplete cart readback'); if (generation === state.generation) latestCart = cart; })
          .catch(() => { if (generation === state.generation) { verificationFailed = true; status(true); } })
          .finally(() => { verifying = false; schedule(); });
      }
      return;
    }
    if (latestCart && signature(current) !== signature(latestCart)) return;
    unlock();
  };
  const schedule = () => { if (state.pending && !frame) frame = requestAnimationFrame(check); };
  const recover = async generation => {
    if (!state.pending || generation !== state.generation || recovering) return;
    recovering = true;
    try {
      if (document.querySelector('cart-items-component.cart-items-disabled')) { recoveryDeferred = true; status(true); return; }
      const path = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${path}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(5000) });
      if (!response.ok) throw new Error('Cart readback unavailable');
      const cart = await response.json();
      if (!fullCart(cart)) throw new Error('Incomplete cart readback');
      if (generation !== state.generation) return;
      const ids = [...new Set([...document.querySelectorAll('[ref="hdt-cart"][section-id]')].map(n => n.getAttribute('section-id')))].filter(Boolean);
      const sectionId = root()?.getAttribute('section-id');
      if (!sectionId || !ids.length || ids.length > 5) throw new Error('Cart section missing');
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      const context = `${window.location.pathname}?sections=${ids.map(encodeURIComponent).join(',')}${preview ? `&preview_theme_id=${encodeURIComponent(preview)}` : ''}`;
      const sectionResponse = await fetch(context, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(5000) });
      if (!sectionResponse.ok) throw new Error('Cart section unavailable');
      const sections = await sectionResponse.json();
      if (generation !== state.generation) return;
      for (const id of ids) {
        if (typeof sections[id] !== 'string') throw new Error('Cart section unavailable');
        const parsed = new DOMParser().parseFromString(sections[id], 'text/html');
        const sectionCart = JSON.parse(parsed.querySelector('[data-en-cart-state]')?.textContent || '{"item_count":0,"total_price":0,"items":[]}');
        if (signature(cart) !== signature(sectionCart)) throw new Error('Cart still changing');
      }
      keyPlans.clear();
      variantPlans.clear();
      latestCart = cart;
      rendered = false;
      state.recovered += 1;
      document.dispatchEvent(new CustomEvent('cart:update', { detail: { resource: {}, sourceId: sectionId, cartData: { ...cart, sections }, actionAfterATC: 'no_action', source: 'en_pending_recovery', data: {} } }));
      setTimeout(() => { if (generation === state.generation) { check(); if (!state.pending) status(true); } }, 400);
    } catch (_error) { status(true); }
    finally { recovering = false; }
  };
  const begin = () => {
    state.pending = true;
    state.generation += 1;
    lastIntent = performance.now();
    latestCart = null;
    rendered = false;
    recoveryDeferred = false;
    verificationFailed = false;
    clearTimeout(timer);
    const generation = state.generation;
    timer = setTimeout(() => recover(generation), 15000);
    setLocks();
    status();
  };
  const quantityIntent = (input, quantity) => {
    selectSurface(input);
    const item = snapshot()?.items?.[Number(input?.dataset.index) - 1];
    if (!item || !Number.isInteger(quantity) || quantity < 0) return;
    begin();
    keyPlans.set(item.key, quantity);
  };
  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const checkout = target.closest('button[name="checkout"]');
    if (checkout?.closest('hdt-main-cart, hdt-cart-drawer') && state.pending) {
      event.preventDefault(); event.stopImmediatePropagation(); state.blocked += 1; status(); return;
    }
    const remove = target.closest('wrapp-remove-item-oncart');
    if (remove?.closest('cart-items-component')) {
      selectSurface(remove);
      const line = Number(remove.dataset.index);
      const input = remove.closest('hdt-line-item')?.querySelector('input[name="updates[]"]');
      const item = snapshot()?.items?.[line - 1];
      if (input) quantityIntent(input, 0);
      else if (item) { begin(); keyPlans.set(item.key, 0); }
      return;
    }
    const qtyButton = target.closest('button[name="plus"],button[name="minus"]');
    if (qtyButton?.closest('cart-items-component') && !qtyButton.disabled) {
      const input = qtyButton.closest('hdt-quantity-wrapp')?.querySelector('input[name="updates[]"]');
      if (input) {
        const quantity = Number(input.value) + (qtyButton.name === 'plus' ? 1 : -1) * (Number(input.step) || 1);
        if (quantity >= Number(input.min || 0) && (!input.max || quantity <= Number(input.max))) quantityIntent(input, quantity);
      }
      return;
    }
    const add = target.closest('[data-en-upsell-add],[data-en-bottom-add],button[name="add"]');
    if (add && !add.disabled) {
      selectSurface(add);
      const form = add.closest('form');
      const id = Number(add.dataset.variantId || form?.querySelector('[name="id"]')?.value);
      const quantity = Number(form?.querySelector('[name="quantity"]')?.value || 1);
      const current = snapshot();
      if (id && current) {
        const total = current.items.filter(i => Number(i.variant_id || i.id) === id).reduce((n, i) => n + i.quantity, 0);
        begin(); variantPlans.set(id, total + quantity);
      }
    }
  }, true);
  document.addEventListener('change', event => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.name === 'updates[]' && input.closest('cart-items-component')) quantityIntent(input, Number(input.value));
  }, true);
  document.addEventListener('submit', event => {
    if (state.pending && ['CartPage-Form', 'CartDrawer-Form'].includes(event.target.id)) {
      event.preventDefault(); event.stopImmediatePropagation(); state.blocked += 1; status();
    }
  }, true);
  document.addEventListener('cart:update', event => {
    const cart = event.detail?.cartData;
    if (state.pending && fullCart(cart)) latestCart = cart;
    schedule();
  });
  document.addEventListener('cart:updated', event => { if (state.pending) { if (fullCart(event.detail?.cart)) latestCart = event.detail.cart; rendered = true; setTimeout(schedule, 360); } });
  const viewport = () => {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    if (!matchMedia('(max-width:749px)').matches) { drawer.style.removeProperty('--en-cart-viewport-height'); drawer.style.removeProperty('--en-cart-viewport-top'); return; }
    drawer.style.setProperty('--en-cart-viewport-height', `${window.visualViewport?.height || innerHeight}px`);
    drawer.style.setProperty('--en-cart-viewport-top', `${window.visualViewport?.offsetTop || 0}px`);
  };
  ['cart:main:render','cart:drawer:render'].forEach(name => document.addEventListener(name, () => { viewport(); if (state.pending) { rendered = true; setTimeout(schedule, 360); } }));
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class','disabled'] });
  window.addEventListener('resize', viewport, { passive: true });
  window.visualViewport?.addEventListener('resize', viewport, { passive: true });
  window.visualViewport?.addEventListener('scroll', viewport, { passive: true });
  window.addEventListener('online', () => { if (state.pending) recover(state.generation); });
  window.addEventListener('pageshow', () => { viewport(); if (state.pending) recover(state.generation); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', viewport, { once: true });
  else viewport();
})();


